import RetailCategoryPrices from './RetailCategoryPrices';
import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '../PageHeader';
import SearchBar from '../SearchBar';
import AdminDistributorCustomers from './AdminDistributorCustomers';
import AdminCustomerProfile from './AdminCustomerProfile';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import useDebounced from '../../hooks/useDebounced';
import useAccessibleDialog from '../../hooks/useAccessibleDialog';
import { useLocale, useT } from '../../i18n/LanguageContext';
import { HEADER_CELL } from '../../utils/tableColumns';

const COLUMNS =
  'grid-cols-[minmax(116px,3.12fr)_minmax(100px,2.38fr)_minmax(120px,3fr)_minmax(92px,2.31fr)_minmax(56px,1.96fr)_minmax(60px,1.98fr)_minmax(84px,2.21fr)_minmax(96px,2.3fr)_minmax(80px,2.2fr)_88px]';
function CustomerDialog({ selected, distributors, onClose, onSaved, t }) {
  const [settingsLocked, setSettingsLocked] = useState(false);
  const [profileLocked, setProfileLocked] = useState(false);
  const [settingsEpoch, setSettingsEpoch] = useState(0);
  const [name, setName] = useState(selected?.name || '');
  const locked = settingsLocked || profileLocked;
  const [id, setId] = useState(selected?.distributorId || '');
  const close = useCallback(() => {
    if (!locked) onClose();
  }, [locked, onClose]);
  const { dialogRef, titleId } = useAccessibleDialog({
    open: true,
    onClose: close,
    closeDisabled: locked,
  });
  const organization = distributors.find((row) => row._id === id);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 id={titleId} className="text-lg font-semibold">
            {selected ? `${t('Edit customer')} · ${name}` : t('New customer')}
          </h2>
          <button
            type="button"
            disabled={locked}
            onClick={close}
            className="text-sm text-[#0089ff] disabled:opacity-40"
          >
            {t('Close')}
          </button>
        </div>
        {!selected && (
          <label className="block text-sm mb-4">
            {t('Distributor')}
            <select
              className="block border rounded-lg p-2 mt-2"
              value={id}
              disabled={locked}
              onChange={(event) => setId(event.target.value)}
            >
              <option value="">{t('Choose a distributor')}</option>
              {distributors
                .filter((row) => row.isActive !== false && row.independentCustomers)
                .map((row) => (
                  <option key={row._id} value={row._id}>
                    {row.name}
                  </option>
                ))}
            </select>
          </label>
        )}
        {selected && (
          <AdminCustomerProfile
            distributorId={id}
            clinicId={selected._id}
            t={t}
            readOnly={settingsLocked}
            onBusyChange={setProfileLocked}
            onSaved={async (profile) => {
              setName(profile.name);
              setSettingsEpoch((value) => value + 1);
              await onSaved();
            }}
          />
        )}
        {organization &&
        (selected || (organization.isActive !== false && organization.independentCustomers)) ? (
          <div className="me-distributor-admin">
            <AdminDistributorCustomers
              key={`${id}:${settingsEpoch}`}
              distributor={organization}
              initiallyExpanded
              initialClinicId={selected?._id}
              detailOnly={!!selected}
              onLockChange={setSettingsLocked}
              readOnly={profileLocked}
              onSaved={onSaved}
            />
          </div>
        ) : (
          selected && (
            <p className="text-sm text-[#64748b]">
              {selected.distributorName} · {selected.companyName || selected.name} ·{' '}
              {selected.phone || '—'}
            </p>
          )
        )}
        {locked && (
          <p role="status" className="text-sm text-slate-600 mt-4">
            {t('Save or discard your changes before closing.')}
          </p>
        )}
      </div>
    </div>
  );
}

export default function AdminDistributorCustomerList({ distributorId, distributors, filter }) {
  const t = useT(),
    locale = useLocale();
  const [search, setSearch] = useState('');
  const settledSearch = useDebounced(search, 300);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);
  const scope = `${distributorId}:${settledSearch}:${page}`;
  const { begin, version } = useShippingRequest(scope);
  const load = useCallback(
    async ({ background = false } = {}) => {
      const request = begin();
      if (!background) setResult(null);
      setError('');
      try {
        const data = (
          await distributorService.adminCustomerOverview({
            page,
            limit: 20,
            ...(distributorId ? { distributorId } : {}),
            ...(settledSearch.trim() ? { search: settledSearch.trim() } : {}),
          })
        )?.data?.data;
        if (
          !Array.isArray(data?.data) ||
          !Number.isSafeInteger(data.total) ||
          data.total < 0 ||
          data.data.some(
            (row) => !/^[a-f\d]{24}$/.test(row._id) || !/^[a-f\d]{24}$/.test(row.distributorId)
          )
        )
          throw new Error('Invalid customers');
        if (request.isCurrent()) {
          if (page > Math.max(1, Math.ceil(data.total / 20)))
            setPage(Math.max(1, Math.ceil(data.total / 20)));
          else setResult({ ...data, scope, version });
        }
      } catch {
        if (request.isCurrent()) setError('Could not load distributor customers. Please retry.');
      }
    },
    [begin, page, distributorId, settledSearch, scope, version]
  );
  useEffect(() => {
    load();
  }, [load]);
  const data = result?.scope === scope && result?.version === version ? result : null;
  const close = useCallback(() => setDialog(null), []);
  const amount = (value) =>
    typeof value === 'number'
      ? value.toLocaleString(locale, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 4,
        })
      : '—';
  const cell = 'text-xs font-medium text-[#64748b] truncate';
  return (
    <div>
      <PageHeader
        title="Distributor Customers"
        subtitle="Manage your practices and their account settings."
      >
        <SearchBar
          placeholder="Search customers..."
          label="Search customers"
          value={search}
          className="w-[243px]"
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
        {filter}
        <button
          type="button"
          disabled={!distributors.some((row) => row.isActive !== false && row.independentCustomers)}
          onClick={() => setDialog({ version })}
          className="h-[46px] px-4 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] disabled:opacity-40"
        >
          {t('New customer')}
        </button>
      </PageHeader>
      {error && (
        <p
          role="alert"
          className="mb-4 p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600"
        >
          {t(error)}{' '}
          <button className="underline" onClick={load}>
            {t('Retry')}
          </button>
        </p>
      )}
      {!data && !error && <p role="status">{t('Loading clinics...')}</p>}
      {data && (
        <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-x-auto">
          <div role="table" aria-label={t('Distributor Customers')} className="min-w-[1080px]">
            <div
              role="rowgroup"
              className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px]"
            >
              <div role="row" className={`grid ${COLUMNS} px-5 py-4`}>
                {[
                  'NAME',
                  'COMPANY',
                  'Distributor',
                  'PHONE',
                  'STATUS',
                  'PRACTICE',
                  'Retail pricing',
                  'CREDIT LIMIT',
                  'LAST SYNCED',
                  'ACTIONS',
                ].map((col) => (
                  <span role="columnheader" key={col} className={HEADER_CELL}>
                    {t(col)}
                  </span>
                ))}
              </div>
            </div>
            <div role="rowgroup" className="divide-y divide-[#e2e8f0]">
              {!data.data.length && (
                <div role="row" className="px-5 py-8 text-center text-sm text-[#64748b]">
                  <span role="cell">{t('No customers match your search.')}</span>
                </div>
              )}
              {data.data.map((row) => (
                <div
                  role="row"
                  key={`${row.distributorId}:${row._id}`}
                  className={`grid ${COLUMNS} px-5 py-4 items-center hover:bg-gray-50/50 transition-colors`}
                >
                  <span role="cell" className="text-xs font-medium text-[#0f172a]" title={row.name}>
                    {row.name}
                  </span>
                  <span role="cell" className={cell} title={row.companyName}>
                    {row.companyName || '—'}
                  </span>
                  <span role="cell" className={cell} title={row.distributorName}>
                    {row.distributorName}
                  </span>
                  <span role="cell" className={cell}>
                    {row.phone || '—'}
                  </span>
                  <span role="cell">
                    <span
                      className={`inline-flex px-2.5 py-0.5 rounded-[6px] text-xs font-medium ${row.isActive !== false ? 'bg-[rgba(222,252,237,0.6)] text-[#007a55]' : 'bg-[rgba(254,226,226,0.6)] text-[#f23e41]'}`}
                    >
                      {t(row.isActive !== false ? 'Active' : 'Inactive')}
                    </span>
                  </span>
                  <span role="cell" className={cell}>
                    {t(
                      row.isPractice === true
                        ? 'Practice'
                        : row.isPractice === false
                          ? 'Hidden'
                          : 'Unreviewed'
                    )}
                  </span>
                  <span role="cell" className={cell}>
                    <RetailCategoryPrices settings={row} t={t} />
                  </span>
                  <span role="cell" className={cell}>
                    {amount(row.creditLimit)}
                  </span>
                  <span role="cell" className={cell}>
                    {row.qbSyncedAt ? new Date(row.qbSyncedAt).toLocaleString(locale) : '—'}
                  </span>
                  <span role="cell">
                    <button
                      type="button"
                      onClick={() => setDialog({ row, version })}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[6px] border border-[#e2e8f0] text-xs font-medium text-[#0f172a] hover:bg-gray-50"
                    >
                      <span
                        className="material-symbols-outlined text-[16px] text-[#64748b]"
                        aria-hidden="true"
                      >
                        edit
                      </span>
                      {t('Edit')}
                    </button>
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center px-5 py-3 border-t border-[#e2e8f0] bg-[rgba(226,232,240,0.15)] text-xs text-[#64748b]">
              <span>
                {t('Showing {first}–{last} of {total}', {
                  first: data.total ? (page - 1) * 20 + 1 : 0,
                  last: Math.min(page * 20, data.total),
                  total: data.total,
                })}
              </span>
              {data.total > 20 && (
                <div className="flex items-center gap-2">
                  <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    {t('Previous')}
                  </button>
                  <span>
                    {t('Page {page} of {total}', { page, total: Math.ceil(data.total / 20) })}
                  </span>
                  <button
                    disabled={page >= Math.ceil(data.total / 20)}
                    onClick={() => setPage(page + 1)}
                  >
                    {t('Next')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {dialog?.version === version && (
        <CustomerDialog
          selected={dialog.row}
          distributors={distributors}
          onClose={close}
          onSaved={() => load({ background: true })}
          t={t}
        />
      )}
    </div>
  );
}
